/**
 * QRCodeFloatingPanel - Draggable floating QR Code panel
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import QRCodePlaceholder from '../qrCodePlaceholder';

// Available QR sizes - more zoom levels
const QR_SIZES = [80, 120, 160, 200, 240, 280, 320, 360, 400, 450, 500];

function QRCodeFloatingPanel({ 
  sessionCode, 
  onClose,
  initialPosition = { x: 20, y: 100 }
}) {
  const [qrCodeSize, setQrCodeSize] = useState(200);
  const [position, setPosition] = useState(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const currentSizeIndex = QR_SIZES.indexOf(qrCodeSize);

  const decreaseSize = () => {
    if (currentSizeIndex > 0) {
      setQrCodeSize(QR_SIZES[currentSizeIndex - 1]);
    }
  };

  const increaseSize = () => {
    if (currentSizeIndex < QR_SIZES.length - 1) {
      setQrCodeSize(QR_SIZES[currentSizeIndex + 1]);
    }
  };

  const handleMouseDown = (e) => {
    // Don't start drag on buttons or inputs
    if (e.target.closest('button') || e.target.closest('input')) return;
    
    setIsDragging(true);
    setDragOffset({ 
      x: e.clientX - position.x, 
      y: e.clientY - position.y 
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({ 
      x: e.clientX - dragOffset.x, 
      y: e.clientY - dragOffset.y 
    });
  };

  const handleMouseUp = () => setIsDragging(false);
  const handleMouseLeave = () => setIsDragging(false);

  return (
    <div 
      className="fixed bg-gray-800 rounded-xl shadow-2xl border border-gray-600 z-50 select-none"
      style={{ 
        left: position.x, 
        top: position.y,
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
    >
      {/* Header with Drag Handle */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-700 rounded-t-xl border-b border-gray-600">
        <span className="text-sm text-gray-300 flex items-center gap-2">
          <span className="cursor-grab">⋮⋮</span> QR-Code
        </span>
        <button
          onClick={onClose}
          className="w-6 h-6 rounded hover:bg-gray-600 text-gray-400 hover:text-white flex items-center justify-center"
        >✕</button>
      </div>
      
      {/* QR Content */}
      <div className="p-3 flex flex-col items-center">
        <div className="bg-white p-2 rounded-lg mb-3" style={{ transition: 'all 0.2s ease' }}>
          <QRCodePlaceholder sessionId={sessionCode} size={qrCodeSize} />
        </div>
        <div className="font-mono text-lg font-bold text-white bg-gray-700 py-2 px-4 rounded text-center mb-3" style={{ width: qrCodeSize + 16 }}>
          {sessionCode}
        </div>
        
        {/* Size controls below QR code - centered */}
        <div className="flex items-center gap-2">
          <button
            onClick={decreaseSize}
            disabled={currentSizeIndex === 0}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              currentSizeIndex === 0 
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed' 
                : 'bg-primary-600 hover:bg-primary-700 text-white'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </button>
          <span className="text-sm text-gray-300 font-medium min-w-[4rem] text-center">{qrCodeSize}px</span>
          <button
            onClick={increaseSize}
            disabled={currentSizeIndex === QR_SIZES.length - 1}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              currentSizeIndex === QR_SIZES.length - 1 
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed' 
                : 'bg-primary-600 hover:bg-primary-700 text-white'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

QRCodeFloatingPanel.propTypes = {
  sessionCode: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  initialPosition: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number
  })
};

export default QRCodeFloatingPanel;
